import type * as React from "react";
import type { TFunction } from "i18next";
import {
  Building2,
  Calendar,
  ClipboardList,
  History,
  Home,
  Inbox,
  Languages,
  Mail,
  MapPin,
  ScrollText,
  Settings,
  UserCheck,
  Users,
} from "lucide-react";
import { SidebarNavItem } from "@/components/vroom/sidebar-nav-item";
import { BottomNavItem } from "@/components/vroom/bottom-nav-item";
import type { SidebarSection } from "@/components/vroom/sidebar";

export type AppSection = "worker" | "admin";

export const WORKER_HOME = "/work" as const;
export const ADMIN_HOME = "/admin/uren" as const;

export type AppSidebarActiveKey =
  | "overzicht"
  | "inbox"
  | "changes"
  | "uren"
  | "cost-centers"
  | "users"
  | "replacements"
  | "addresses"
  | "holidays"
  | "audit-log"
  | "email-templates"
  | "translations"
  | "app-settings";

export interface AppSidebarArgs {
  active: AppSidebarActiveKey;
  role: "worker" | "approver" | "super_admin";
  costCenterCount?: number;
}

export function appSidebarSections(
  { active, role, costCenterCount }: AppSidebarArgs,
  t: TFunction,
): SidebarSection[] {
  const sections: SidebarSection[] = [
    {
      label: t("common.nav.work.label"),
      items: (
        <>
          <SidebarNavItem
            to="/work"
            icon={<Home size={14} />}
            label={t("admin.sidebar.overview.label")}
            active={active === "overzicht"}
          />
          <SidebarNavItem
            to="/work/inbox"
            icon={<Inbox size={14} />}
            label={t("admin.sidebar.inbox.label")}
            active={active === "inbox"}
          />
          <SidebarNavItem
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to={"/work/changes" as any}
            icon={<History size={14} />}
            label={t("work.nav.changes")}
            active={active === "changes"}
          />
        </>
      ),
    },
  ];

  if (role === "super_admin" || role === "approver") {
    sections.push({
      label: t("common.nav.admin.label"),
      items: (
        <>
          <SidebarNavItem
            to="/admin/uren"
            icon={<ClipboardList size={14} />}
            label={t("admin.sidebar.uren.label")}
            active={active === "uren"}
          />
          {role === "super_admin" && (
            <>
              <SidebarNavItem
                to="/dashboard"
                icon={<Building2 size={14} />}
                label={t("admin.sidebar.cost-centers.label")}
                count={costCenterCount}
                active={active === "cost-centers"}
              />
              <SidebarNavItem
                to="/admin/users"
                icon={<Users size={14} />}
                label={t("admin.sidebar.users.label")}
                active={active === "users"}
              />
              <SidebarNavItem
                to="/admin/replacements"
                icon={<UserCheck size={14} />}
                label={t("admin.sidebar.replacements.label")}
                active={active === "replacements"}
              />
              <SidebarNavItem
                to="/admin/addresses"
                icon={<MapPin size={14} />}
                label={t("admin.sidebar.addresses.label")}
                active={active === "addresses"}
              />
              <SidebarNavItem
                to="/admin/holidays"
                icon={<Calendar size={14} />}
                label={t("admin.sidebar.holidays.label")}
                active={active === "holidays"}
              />
            </>
          )}
        </>
      ),
    });
  }

  if (role === "super_admin") {
    sections.push({
      label: t("admin.sidebar.system.label"),
      items: (
        <>
          <SidebarNavItem
            to="/admin/audit-log"
            icon={<ScrollText size={14} />}
            label={t("admin.sidebar.audit-log.label")}
            active={active === "audit-log"}
          />
          <SidebarNavItem
            to="/admin/email-templates"
            icon={<Mail size={14} />}
            label={t("admin.sidebar.email-templates.label")}
            active={active === "email-templates"}
          />
          <SidebarNavItem
            to="/admin/translations"
            icon={<Languages size={14} />}
            label={t("admin.sidebar.translations.label")}
            active={active === "translations"}
          />
          <SidebarNavItem
            to="/admin/app-settings"
            icon={<Settings size={14} />}
            label={t("admin.sidebar.app-settings.label")}
            active={active === "app-settings"}
          />
        </>
      ),
    });
  }

  return sections;
}

export interface AppBottomNavArgs {
  section: AppSection;
  active: AppSidebarActiveKey;
  role: "worker" | "approver" | "super_admin";
}

export function appBottomNavItems(
  { section, active, role }: AppBottomNavArgs,
  t: TFunction,
): React.ReactElement[] {
  if (section === "worker") {
    return [
      <BottomNavItem
        key="overzicht"
        to="/work"
        icon={<Home size={20} />}
        label={t("admin.sidebar.overview.label")}
        active={active === "overzicht"}
      />,
      <BottomNavItem
        key="inbox"
        to="/work/inbox"
        icon={<Inbox size={20} />}
        label={t("admin.sidebar.inbox.label")}
        active={active === "inbox"}
      />,
      <BottomNavItem
        key="changes"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        to={"/work/changes" as any}
        icon={<History size={20} />}
        label={t("work.nav.changes")}
        active={active === "changes"}
      />,
    ];
  }
  const items: React.ReactElement[] = [
    <BottomNavItem
      key="uren"
      to="/admin/uren"
      icon={<ClipboardList size={20} />}
      label={t("admin.sidebar.uren.label")}
      active={active === "uren"}
    />,
  ];
  if (role === "super_admin") {
    items.push(
      <BottomNavItem
        key="users"
        to="/admin/users"
        icon={<Users size={20} />}
        label={t("admin.sidebar.users.label")}
        active={active === "users"}
      />,
      <BottomNavItem
        key="replacements"
        to="/admin/replacements"
        icon={<UserCheck size={20} />}
        label={t("admin.sidebar.replacements.label")}
        active={active === "replacements"}
      />,
      <BottomNavItem
        key="cost-centers"
        to="/dashboard"
        icon={<Building2 size={20} />}
        label={t("admin.sidebar.cost-centers.label")}
        active={active === "cost-centers"}
      />,
      <BottomNavItem
        key="addresses"
        to="/admin/addresses"
        icon={<MapPin size={20} />}
        label={t("admin.sidebar.addresses.label")}
        active={active === "addresses"}
      />,
    );
  }
  return items;
}
